import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Icon } from "@iconify-icon/react";
import { ArrowRight, CheckCircle2, Link, Mail, Users } from "lucide-react";

import { InviteForm } from "./InviteForm.tsx";

import { Button } from "@/components/ui/button";

interface InviteStepProps {
  onNext: () => void;
  onSkip: () => void;
}

const InviteStep: React.FC<InviteStepProps> = ({ onNext, onSkip }) => {
  const { t } = useTranslation(["onboarding", "workspaces", "help"]);
  const [currentView, setCurrentView] = useState<"intro" | "invite">("intro");
  const [selectedInviteType, setSelectedInviteType] = useState<"link" | "email">("link");

  // Animation variants for the tip cards
  const tipVariants = {
    initial: { opacity: 0, scale: 0.9 },
    animate: (custom: number) => ({
      opacity: 1,
      scale: 1,
      transition: { delay: 0.1 * custom, duration: 0.3 },
    }),
  };

  const tips = [
    {
      title: t("help:tutorInvitations.step1.title", "Start Building Your Class"),
      description: t(
        "help:tutorInvitations.step1.description",
        "Now that you have set up your pricing, it's time to invite students to your workspace. This is where you'll create your virtual classroom and start teaching.",
      ),
      icon: "tabler:school",
      color: "from-green-500/20 to-emerald-500/20",
    },
    {
      title: t("help:tutorInvitations.step2.title", "Easy Invitation Process"),
      description: t(
        "help:tutorInvitations.step2.description",
        "Generate invitation links that you can share with students via email, messaging, or any communication method. Students can join your workspace with just one click.",
      ),
      icon: "tabler:link",
      color: "from-blue-500/20 to-cyan-500/20",
    },
    {
      title: t("help:tutorInvitations.step3.title", "Manage Your Students"),
      description: t(
        "help:tutorInvitations.step3.description",
        "Once students join, you can manage their access, view their progress, and organize your teaching materials all in one place.",
      ),
      icon: "tabler:users-group",
      color: "from-purple-500/20 to-violet-500/20",
    },
    {
      title: t("onboarding:invite.tip4.title", "Optional Step"),
      description: t(
        "onboarding:invite.tip4.description",
        "This step is completely optional. You can always invite students later from your workspace dashboard.",
      ),
      icon: "tabler:clock",
      color: "from-orange-500/20 to-amber-500/20",
    },
  ];

  const handleStartInviting = () => {
    setCurrentView("invite");
  };

  const handleInviteComplete = () => {
    // Clear the localStorage flag since user completed the invite flow
    localStorage.removeItem("should_show_invite_help");
    onNext();
  };

  const handleSkip = () => {
    // Clear the localStorage flag since user skipped
    localStorage.removeItem("should_show_invite_help");
    onSkip();
  };

  if (currentView === "invite") {
    return (
      <div className="relative flex h-full w-full items-center justify-center p-2 sm:p-4">
        <div className="relative z-10 mb-20 flex h-full w-full max-w-7xl items-center">
          <InviteForm
            selectedType={selectedInviteType}
            onBack={() => setCurrentView("intro")}
            onComplete={handleInviteComplete}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full space-y-8 max-sm:p-2">
      {/* Background circles */}
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 opacity-30 transition-colors duration-700"
        initial={{ opacity: 0 }}
        layoutId="circle1"
        transition={{
          default: { duration: 0.75 },
          layout: { duration: 0.75 },
        }}
      />
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="absolute -top-50 -right-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 opacity-30 transition-colors duration-700"
        initial={{ opacity: 0.2 }}
        layoutId="circle2"
        transition={{
          layout: { duration: 0.75 },
        }}
      />

      <div className="relative z-10 grid h-full w-full max-w-7xl grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left side: Tips - Hidden on mobile */}
        <div className="hidden lg:col-span-1 lg:flex lg:flex-col lg:justify-center lg:space-y-4">
          {tips.map((tip, index) => (
            <motion.div
              key={index}
              animate="animate"
              className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 backdrop-blur-sm"
              custom={index + 1}
              initial="initial"
              variants={tipVariants}
            >
              <div
                className={`absolute top-1/2 left-0 h-full w-full -translate-x-1/6 -translate-y-1/2 rounded-full bg-gradient-to-br blur-3xl transition-colors duration-700 ${tip.color}`}
              />
              <div className="relative z-50 flex items-start gap-3">
                <div className="bg-accent-foreground/20 mt-1 flex shrink-0 items-center rounded-full p-2">
                  <Icon
                    className="text-accent-foreground"
                    height="1rem"
                    icon={tip.icon}
                    width="1rem"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm leading-tight font-semibold">{tip.title}</h3>
                  <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                    {tip.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Right side: Main Content */}
        <div className="col-span-1 flex items-center justify-center lg:col-span-2">
          <div className="w-full max-w-2xl space-y-6">
            {/* Header */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4 text-center sm:space-y-6"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-muted/30 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full sm:h-16 sm:w-16">
                <Users className="text-accent-foreground h-6 w-6 sm:h-8 sm:w-8" />
              </div>
              <h1 className="text-accent-foreground text-2xl font-bold sm:text-3xl lg:text-4xl">
                {t("onboarding:invite.title", "Invite Students to Your Workspace")}
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg md:text-xl">
                {t(
                  "onboarding:invite.description",
                  "Ready to start teaching? Invite your first students to join your workspace and begin your tutoring journey.",
                )}
              </p>
            </motion.div>

            {/* Invitation Type Selection */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <h3 className="text-accent-foreground text-center text-lg font-semibold sm:text-xl">
                {t("onboarding:invite.chooseMethod", "Choose Your Invitation Method")}
              </h3>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                <motion.button
                  className={`group relative overflow-hidden rounded-xl p-4 text-left transition-all duration-200 sm:p-6 ${
                    selectedInviteType === "link"
                      ? "bg-foreground/50 shadow-xl backdrop-blur-sm"
                      : "bg-foreground/30 hover:bg-foreground/40 backdrop-blur-sm"
                  }`}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  onClick={() => setSelectedInviteType("link")}
                >
                  {/* Background decorative circles */}
                  <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-blue-500/15" />
                    <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-purple-500/15" />
                  </div>
                  <div className="relative flex flex-col items-center gap-3">
                    <motion.div
                      animate={{
                        backgroundColor:
                          selectedInviteType === "link"
                            ? "rgba(59, 130, 246, 0.2)"
                            : "rgba(59, 130, 246, 0.1)",
                        scale: selectedInviteType === "link" ? 1.05 : 1,
                      }}
                      className="flex h-12 w-12 items-center justify-center rounded-xl transition-colors"
                      transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                      <Link className="text-accent-foreground h-6 w-6" />
                    </motion.div>
                    <div className="text-center">
                      <h4 className="text-accent-foreground mb-2 text-lg font-semibold">
                        {t("workspaces:linkInvite", "Link Invite")}
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        {t("workspaces:linkInviteDescription", "Generate a shareable link")}
                      </p>
                    </div>
                  </div>
                  {selectedInviteType === "link" && (
                    <motion.div
                      animate={{ scale: 1, opacity: 1 }}
                      className="absolute top-4 right-4"
                      exit={{ scale: 0, opacity: 0 }}
                      initial={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                      <CheckCircle2 className="h-6 w-6 text-blue-500" />
                    </motion.div>
                  )}
                </motion.button>

                <motion.button
                  className={`group relative overflow-hidden rounded-xl p-4 text-left transition-all duration-200 sm:p-6 ${
                    selectedInviteType === "email"
                      ? "bg-foreground/50 shadow-xl backdrop-blur-sm"
                      : "bg-foreground/30 hover:bg-foreground/40 backdrop-blur-sm"
                  }`}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  onClick={() => setSelectedInviteType("email")}
                >
                  {/* Background decorative circles */}
                  <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-purple-500/15" />
                    <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-indigo-500/15" />
                  </div>
                  <div className="relative flex flex-col items-center gap-3">
                    <motion.div
                      animate={{
                        backgroundColor:
                          selectedInviteType === "email"
                            ? "rgba(139, 92, 246, 0.2)"
                            : "rgba(139, 92, 246, 0.1)",
                        scale: selectedInviteType === "email" ? 1.05 : 1,
                      }}
                      className="flex h-12 w-12 items-center justify-center rounded-xl transition-colors"
                      transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                      <Mail className="text-accent-foreground h-6 w-6" />
                    </motion.div>
                    <div className="text-center">
                      <h4 className="text-accent-foreground mb-2 text-lg font-semibold">
                        {t("workspaces:emailInvite", "Email Invite")}
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        {t(
                          "workspaces:emailInviteDescription",
                          "Search and invite students directly",
                        )}
                      </p>
                    </div>
                  </div>
                  {selectedInviteType === "email" && (
                    <motion.div
                      animate={{ scale: 1, opacity: 1 }}
                      className="absolute top-4 right-4"
                      exit={{ scale: 0, opacity: 0 }}
                      initial={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                      <CheckCircle2 className="h-6 w-6 text-purple-500" />
                    </motion.div>
                  )}
                </motion.button>
              </div>
            </motion.div>

            {/* Primary action */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Button
                className="w-full px-6 sm:px-8"
                size="lg"
                variant="foreground"
                onClick={handleStartInviting}
              >
                {t("onboarding:invite.startInviting", "Start Inviting Students")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>

            {/* Help text with skip link (right-aligned) */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="text-muted-foreground text- flex flex-col justify-between gap-2 text-center sm:flex-row"
              initial={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.4, delay: 0.35 }}
            >
              <span className={"text-sm"}>
                {t(
                  "onboarding:invite.helpText",
                  "Don't worry, you can always invite students later from your workspace dashboard.",
                )}
              </span>
              <Button
                className="text-muted-foreground hover:text-accent-foreground h-auto px-0 underline"
                size="sm"
                variant="link"
                onClick={handleSkip}
              >
                {t("onboarding:invite.skipForNow", "Skip for Now")}
              </Button>
            </motion.div>

            {/* Tips section for mobile - Show below help text */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3 lg:hidden"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {tips.map((tip, index) => (
                  <motion.div
                    key={index}
                    animate="animate"
                    className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 backdrop-blur-sm"
                    custom={index + 1}
                    initial="initial"
                    variants={tipVariants}
                  >
                    <div
                      className={`absolute top-1/2 left-0 h-full w-full -translate-x-1/6 -translate-y-1/2 rounded-full bg-gradient-to-br blur-3xl transition-colors duration-700 ${tip.color}`}
                    />
                    <div className="relative z-50 flex items-start gap-3">
                      <div className="bg-accent-foreground/20 mt-1 flex shrink-0 items-center rounded-full p-2">
                        <Icon
                          className="text-accent-foreground"
                          height="1rem"
                          icon={tip.icon}
                          width="1rem"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm leading-tight font-semibold">{tip.title}</h4>
                        <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                          {tip.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteStep;
